import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { VehicleCard } from '../components/VehicleCard'
import { FleetMap } from '../components/FleetMap'
import { IconCar, IconList, IconMap, IconPlus, IconRefresh, IconSearch } from '../components/Icons'
import { useGetVehiclesQuery } from '../services/vehicle'
import { useVehiclesPoll } from '../hooks/useAuth'
import './Home.css'

export function HomePage() {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'list' | 'map'>('list')
  const [q, setQ] = useState('')
  const { data, isLoading, isFetching, refetch } = useGetVehiclesQuery()
  useVehiclesPoll(refetch)

  const all = data?.data ?? []

  const vehicles = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return all
    return all.filter(
      (v) =>
        v.name?.toLowerCase().includes(needle) ||
        v.plateNumber?.toLowerCase().includes(needle) ||
        v.latestPosition?.address?.toLowerCase().includes(needle)
    )
  }, [all, q])

  return (
    <div className="home">
      <div className="home__search-bar">
        <label className="home__search">
          <IconSearch size={18} className="home__search-ico" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('Rechercher un véhicule...')}
          />
        </label>
        <div className="mob-toggle">
          <button
            type="button"
            className={mode === 'list' ? 'is-on' : undefined}
            onClick={() => setMode('list')}
            aria-label={t('Liste')}
          >
            <IconList size={16} />
          </button>
          <button
            type="button"
            className={mode === 'map' ? 'is-on' : undefined}
            onClick={() => setMode('map')}
            aria-label={t('Carte')}
          >
            <IconMap size={16} />
          </button>
        </div>
        <button
          type="button"
          className="home__refresh"
          onClick={() => void refetch()}
          title={t('Chargement…')}
          disabled={isFetching}
        >
          <IconRefresh size={18} className={isFetching ? 'is-spin' : undefined} />
        </button>
      </div>

      <div className={`home__body${mode === 'map' ? ' home__body--map' : ''}`}>
        {isLoading ? (
          <div className="home__skeleton" aria-busy="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="home__skel-card" />
            ))}
          </div>
        ) : vehicles.length === 0 ? (
          <div className="home__empty">
            <IconCar size={40} />
            <h2>{t('Aucun véhicule à suivre')}</h2>
            <p className="muted">
              {q
                ? 'Aucun résultat pour cette recherche.'
                : 'Ajoutez un véhicule depuis l’application mobile DriveOps.'}
            </p>
          </div>
        ) : mode === 'list' ? (
          <div className="home__list">
            {vehicles.map((v) => (
              <VehicleCard key={v._id} vehicle={v} />
            ))}
          </div>
        ) : (
          <div className="home__map-wrap">
            <FleetMap vehicles={vehicles} height="min(68vh, 720px)" />
            <div className="home__map-strip">
              {vehicles.map((v) => (
                <VehicleCard key={v._id} vehicle={v} compact />
              ))}
            </div>
          </div>
        )}
      </div>

      {!isLoading && all.length > 0 && (
        <button type="button" className="home__fab" title={t('Ajouter un véhicule')} aria-label={t('Ajouter un véhicule')}>
          <IconPlus size={22} />
        </button>
      )}
    </div>
  )
}
